// src/pages/AdsInApp.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { X } from "lucide-react";

/**
 * 간단 URL 정규화:
 * - 스킴 누락 시 https:// 붙임
 * - http/https만 허용 (그 외 스킴은 차단)
 */
function normalizeUrl(raw) {
  if (!raw || typeof raw !== "string") return "https://www.naver.com";
  let u = raw.trim();

  // 스킴이 없으면 https:// 붙이기
  if (!/^https?:\/\//i.test(u)) u = "https://" + u;

  try {
    const url = new URL(u);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return "https://www.naver.com";
    }
    return url.toString();
  } catch {
    return "https://www.naver.com";
  }
}

export default function AdsInApp() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const iframeRef = useRef(null);

  const targetUrl = useMemo(
    () => normalizeUrl(searchParams.get("url") || "https://www.naver.com"),
    [searchParams]
  );

  const [pageTitle, setPageTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(15); // 가짜 진행률 시작치

  // 가짜 진행바: 로드 완료 전까지만 살짝 진행
  useEffect(() => {
    if (!loading) {
      setProgress(100);
      const t = setTimeout(() => setProgress(0), 300);
      return () => clearTimeout(t);
    }
    setProgress(15);
    const id = setInterval(() => {
      setProgress((p) => (p < 85 ? p + 5 : p));
    }, 200);
    return () => clearInterval(id);
  }, [loading]);

  const handleClose = () => {
    // 뒤로갈 수 있으면 pop, 아니면 안전한 기본 경로로 이동
    if (window.history.length > 1) navigate(-1);
    else navigate("/ads/main", { replace: true });
  };

  const handleReload = () => {
    try {
      // 같은 URL 재적용으로 리로드
      if (iframeRef.current?.src) {
        iframeRef.current.src = iframeRef.current.src;
      }
    } catch {
      // noop
    }
  };


  const onLoad = () => {
    setLoading(false);
    // 교차출처면 title 접근이 막힐 수 있음 → try/catch
    try {
      const t =
        iframeRef.current?.contentDocument?.title ||
        iframeRef.current?.contentWindow?.document?.title ||
        "";
      if (t) setPageTitle(t);
    } catch {
      // 접근 불가면 URL 호스트만 노출
      try {
        setPageTitle(new URL(targetUrl).host);
      } catch {
        setPageTitle("");
      }
    }
  };

  return (
    <div className="flex flex-col w-full h-[100dvh] bg-white">
      {/* 상단 헤더 */}
      <div className="relative border-b border-gray-200">
        <div className="flex items-center gap-2 px-3 py-2">
          {/* 닫기 */}
          <button
            onClick={handleClose}
            className="px-2.5 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>

          {/* 뒤/앞/새로고침 */}
          <div className="flex items-center gap-1 ml-1">
            
            <button
              onClick={handleReload}
              className="px-2.5 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm"
              title="새로고침"
            >
              ↻
            </button>
          </div>

          {/* 제목/주소 */}
          <div className="flex-1 min-w-0 mx-2">
            <div className="text-sm font-medium truncate">
              {pageTitle || new URL(targetUrl).host}
            </div>
            <div className="text-[11px] text-gray-500 truncate">{targetUrl}</div>
          </div>

          {/* 외부 열기 */}

        </div>

        {/* 로딩 진행바 */}
        {!!progress && (
          <div className="absolute left-0 bottom-0 h-[2px] w-full bg-gray-100">
            <div
              className="h-[2px] bg-[#7864F9] transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>

      {/* 본문(iframe) */}
      <div className="flex-1 overflow-hidden">
        <iframe
          ref={iframeRef}
          title="In-App Browser"
          src={targetUrl}
          onLoad={onLoad}
          // iOS 스크롤/바운스 부드럽게
          className="w-full h-full overflow-auto"
          style={{ WebkitOverflowScrolling: "touch" }}
          // 보안 상 안전한 기본 권한만 허용(필요 시 feature 추가)
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          allow="fullscreen; geolocation; camera; microphone"
        />
      </div>
    </div>
  );
}
